import { useEffect, useState } from "react";
import api from "@/lib/api";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function fmtMoney(n) {
  if (n >= 1000) return "$" + (n / 1000).toFixed(1) + "k";
  return "$" + n.toFixed(0);
}

/** GitHub-style daily revenue heatmap. */
export default function RevenueCalendar() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    setData(null);
    api.get(`/farm/analytics/revenue-calendar?days=${days}`)
      .then(r => setData(r.data))
      .catch(() => setData({ rows: [], total_revenue: 0, total_units: 0, max_revenue: 0 }));
  }, [days]);

  if (!data) return <div className="card-flat p-6 text-[var(--ff-text-muted)]" data-testid="revenue-calendar-loading">Loading calendar…</div>;

  const rows = data.rows || [];
  // Bucket into weeks (cols). Each column = 7 days (Sun → Sat).
  const startDate = rows.length ? new Date(rows[0].date + "T00:00:00") : new Date();
  const startWeekday = startDate.getDay(); // 0=Sun
  const cells = Array(startWeekday).fill(null).concat(rows);
  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) {
    weeks.push(cells.slice(i, i + 7));
  }
  const max = data.max_revenue || 1;
  const intensity = (rev) => {
    if (rev <= 0) return 0;
    const r = rev / max;
    if (r > 0.75) return 4;
    if (r > 0.5) return 3;
    if (r > 0.25) return 2;
    return 1;
  };
  const COLORS = ["#F1F0EC", "#C7DDD0", "#94BCA6", "#5A937B", "#1A4331"];

  // Compute month-label positions
  const monthLabels = [];
  let lastMonth = -1;
  weeks.forEach((wk, wi) => {
    const firstCell = wk.find(c => c);
    if (firstCell) {
      const m = new Date(firstCell.date + "T00:00:00").getMonth();
      if (m !== lastMonth) {
        monthLabels.push({ idx: wi, label: MONTHS[m] });
        lastMonth = m;
      }
    }
  });

  return (
    <div className="card-flat p-5" data-testid="revenue-calendar">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <p className="overline">Revenue calendar</p>
          <h3 className="font-display text-2xl mt-1">Daily sales heatmap</h3>
          <p className="text-xs text-[var(--ff-text-muted)] mt-1">
            <strong className="text-[var(--ff-text-primary)]">{fmtMoney(data.total_revenue)}</strong> total · {data.total_units} units sold · last {data.days} days
          </p>
        </div>
        <div className="flex gap-1 shrink-0">
          {[30, 90, 180, 365].map(n => (
            <button
              key={n}
              onClick={() => setDays(n)}
              className={`px-2.5 py-1 rounded-full text-[11px] ${days === n ? "bg-[var(--ff-primary)] text-white" : "border border-[var(--ff-border)] text-[var(--ff-text-muted)]"}`}
              data-testid={`revcal-range-${n}`}
            >{n}d</button>
          ))}
        </div>
      </div>

      <div className="mt-5 overflow-x-auto">
        <div className="inline-flex flex-col gap-1 min-w-0" data-testid="revcal-grid">
          {/* Month labels */}
          <div className="flex gap-[3px] ml-6 text-[10px] text-[var(--ff-text-muted)] h-3">
            {weeks.map((_, wi) => {
              const lbl = monthLabels.find(m => m.idx === wi);
              return <div key={wi} className="w-3 shrink-0">{lbl ? lbl.label : ""}</div>;
            })}
          </div>
          <div className="flex gap-[3px]">
            <div className="flex flex-col justify-around text-[10px] text-[var(--ff-text-muted)] mr-1 h-[110px]">
              <span>Mon</span><span>Wed</span><span>Fri</span>
            </div>
            <div className="flex gap-[3px]">
              {weeks.map((wk, wi) => (
                <div key={wi} className="flex flex-col gap-[3px]">
                  {Array(7).fill(null).map((_, di) => {
                    const cell = wk[di];
                    if (!cell) return <div key={di} className="w-3 h-3"/>;
                    const lvl = intensity(cell.revenue);
                    return (
                      <button
                        key={di}
                        type="button"
                        className="w-3 h-3 rounded-sm focus:outline-none focus:ring-1 focus:ring-[var(--ff-primary)]"
                        style={{ background: COLORS[lvl] }}
                        onMouseEnter={() => setHover(cell)}
                        onMouseLeave={() => setHover(null)}
                        onFocus={() => setHover(cell)}
                        onBlur={() => setHover(null)}
                        title={`${cell.date}: ${fmtMoney(cell.revenue)} · ${cell.units} units`}
                        data-testid={`revcal-cell-${cell.date}`}
                        aria-label={`${cell.date}: ${cell.revenue} dollars in revenue`}
                      />
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
          {/* Legend */}
          <div className="flex items-center gap-2 mt-2 text-[10px] text-[var(--ff-text-muted)]">
            <span>Less</span>
            {COLORS.map((c, i) => (
              <span key={i} className="w-3 h-3 rounded-sm" style={{ background: c }}/>
            ))}
            <span>More</span>
            {hover && (
              <span className="ml-auto text-[var(--ff-text-secondary)]" data-testid="revcal-hover">
                <strong>{hover.date}</strong> · <strong className="text-[var(--ff-primary)]">{fmtMoney(hover.revenue)}</strong> · {hover.units} units · {hover.orders} orders
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
